import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {ProfileMain} from 'src/components/profile-tab-structure';
import {SaveWithReasonButton} from 'src/components/save-with-reason/save-with-reason-button';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';

import {PersonalizationTabs} from '../personalization-tabs';

import {useLcdSubscriptions} from './hooks/useLcdSubscription';
import {useLcdSubscriptionTrees} from './hooks/useLcdSubscriptionTrees';
import {LcdSubscriptionsView} from './lcd-subscriptions-view';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';


type Props = {
    userId: number,
    accountId: number,
};

const TAB_NAME = 'LCD Credit Analysis';

export const LcdCreditAnalysis = ({userId, accountId}: Props) => {
    const {canEditUser} = useAdminAccessRight();
    const [busy, setBusy] = useState<boolean>(false);
    const {userSubmit, userFieldChange} = useEventTracking();
    const {setSuccessMessage, setErrorMessage} = useContext(MessageContext);

    const {isCurrentChunkChanged, setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'user-lcd-credit-analysis',
        onProceed: () => {
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        },
    });

    const {data: trees} = useLcdSubscriptionTrees();

    const {
        isLoading: isSubscriptionsLoading,
        loadingError: subscriptionsLoadingError,
        isChanged: isSubscriptionsChanged,
        data: subscriptions,
        setData: setSubscriptions,
        saveChanges: saveSubscriptions,
        discardChanges: discardSubscriptions,
    } = useLcdSubscriptions({userId});

    useEffect(() => {
        setIsChunkChanged(isSubscriptionsChanged);
    }, [isSubscriptionsChanged, setIsChunkChanged]);

    const editDisabled = useMemo(() => !canEditUser(accountId), [canEditUser, accountId]);

    const saveChanges = useCallback(async (options?: SaveOptions) => {
        const calls = [];

        setBusy(true);
        try {
            if (isSubscriptionsChanged) {
                calls.push(await saveSubscriptions(options));
            }
            setBusy(false);

            const errors = [...new Set(calls.filter(Boolean))];
            if (errors.length) {
                setErrorMessage(errors.join('\n'));
                return errors.join('\n');
            } else {
                proceed();
            }
            userSubmit({submit: true, place: TAB_NAME});
        } finally {
            setBusy(false);
        }
    }, [
        isSubscriptionsChanged,
        saveSubscriptions,
        proceed,
        setErrorMessage,
        userSubmit,
    ]);

    const discardChanges = useCallback(() => {
        userSubmit({submit: false, place: TAB_NAME});

        discardSubscriptions();
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [discardSubscriptions, setSuccessMessage, userSubmit]);

    const onFieldChange = useCallback((section: string) => (parameterName: string) => {
        userFieldChange({
            section,
            viewType: 'Existed',
            parameterName,
        });
    }, [userFieldChange]);

    return (
        <>
            <PersonalizationTabs userId={userId} activeTab="lcd-credit-analysis"/>
            <ProfileMain header={(
                <Item grow={1}>
                    <Group justify="space-between">
                        <Typography
                            fontWeight="semi-bold"
                            variant="heading-level-3">
                            {TAB_NAME}
                        </Typography>
                        {!editDisabled ? (
                            <Group>
                                <Button
                                    variant="Type.SECONDARY"
                                    onClick={discardChanges}
                                    disabled={busy || !isCurrentChunkChanged}>
                                    Discard Changes
                                </Button>
                                <SaveWithReasonButton
                                    busy={busy}
                                    disabled={!isCurrentChunkChanged}
                                    onSave={saveChanges}
                                />
                            </Group>
                        ) : null}
                    </Group>
                </Item>
            )}>
                <LcdSubscriptionsView
                    disabled={editDisabled}
                    isLoading={isSubscriptionsLoading}
                    loadingError={subscriptionsLoadingError}
                    trees={trees}
                    subscriptions={subscriptions}
                    setSubscriptions={setSubscriptions}
                    onFieldChange={onFieldChange('LCD Credit Analysis')}
                />
            </ProfileMain>
        </>
    );
};
